/**
 * Account query hooks (PHASE1 §7.4) — read-only; mutations live in FE-3.
 *
 * Two caches: `['accounts']` holds every account the server will show without
 * being asked (closed/`archived` included, hidden EXCLUDED server-side) plus the
 * net-worth total and the hidden count from the response `meta`;
 * `['accountsHidden']` is fetched lazily by the „Inaktive Konten" section.
 */
import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type { AccountOut, Money } from '../types.ts';

const STALE = 5 * 60 * 1000;

export interface AccountsData {
  items: AccountOut[];
  /** Σ current_balance over non-archived accounts, from response meta. */
  totalBalance: Money | null;
  /** How many hidden accounts exist — `items` never contains them, so the
   * „Inaktive Konten" section can only size itself from the response meta. */
  hiddenCount: number;
}

/** Raw `['accounts']` query — list (closed included, hidden excluded) + meta. */
export function useAccountsQuery(): UseQueryResult<AccountsData> {
  return useQuery({
    queryKey: qk.accounts,
    queryFn: async () => {
      const envelope = await api.getEnvelope<AccountOut[]>('/api/accounts?include_archived=true');
      return {
        items: envelope.data,
        totalBalance: envelope.meta?.total_balance ?? null,
        hiddenCount: Number(envelope.meta?.hidden_count ?? 0),
      } satisfies AccountsData;
    },
    staleTime: STALE,
  });
}

/** Account list; archived excluded unless `includeArchived`. */
export function useAccounts(includeArchived = false): AccountOut[] {
  const { data } = useAccountsQuery();
  return useMemo(() => {
    const items = data?.items ?? [];
    const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    return includeArchived ? sorted : sorted.filter((a) => !a.archived);
  }, [data, includeArchived]);
}

/**
 * Hidden accounts for the Konten page's „Inaktive Konten" section — lazy, only
 * fetched while `enabled` (the section toggle), unlike the always-on closed
 * accounts, which the primary list already carries.
 *
 * `include_archived=true` is required, not cosmetic: the two server-side
 * filters are independent AND a wiped suppression tombstone is written
 * `hidden=true, archived=true` (§5.7), so `include_hidden=true` alone would
 * silently drop exactly the „Entfernt" rows this section must show. The
 * client-side `a.hidden` filter keeps plain archived accounts out again.
 */
export function useHiddenAccountsQuery(enabled: boolean): UseQueryResult<AccountOut[]> {
  return useQuery({
    queryKey: qk.accountsHidden,
    queryFn: async () => {
      const envelope = await api.getEnvelope<AccountOut[]>(
        '/api/accounts?include_hidden=true&include_archived=true',
      );
      return envelope.data.filter((a) => a.hidden);
    },
    enabled,
    staleTime: STALE,
  });
}

/** id→account map over ALL accounts (archived included). */
export function useAccountMap(): Map<number, AccountOut> {
  const { data } = useAccountsQuery();
  return useMemo(() => {
    const map = new Map<number, AccountOut>();
    for (const account of data?.items ?? []) map.set(account.id, account);
    return map;
  }, [data]);
}
